import { Button, makeStyles } from '@material-ui/core';
import { Videocam } from '@material-ui/icons';
import { useRouterStore } from 'mobx-state-router';
import React, { useCallback, useContext, useState } from 'react';

import Tooltip from 'src/components/general/Tooltip';
import { SideVisitContext } from 'src/components/pages/eventShow/SideVisitContext';
import ConfirmDialog from 'src/components/pages/pageElements/confirmDialog';
import { ApolloClientContext } from 'src/data/ApolloClientContext';
import { ACCEPT_MUTATION } from 'src/dropInClinic/queries';
import { AcceptMutationArgs, AcceptMutationResponse } from 'src/dropInClinic/types';
import { OBSERVABILITY_MY_DAY_TODAY } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { IntakePrepResponsibility } from 'src/generated/gql/graphql';
import { useProviderId } from 'src/myDayToday/domain/useProviderId';
import { REFRESH_SCHEDULE_MUTATION } from 'src/myDayToday/queries/refreshSchedule.gql';
import { useObservability } from 'src/observability/useObservability';
import { BoulderColors } from 'src/util/brand';

type AcceptPopInSuggestionProps = {
  requestId: string;
  prepResponsibility?: IntakePrepResponsibility;
  disabled?: boolean;
  onScheduleRefresh: () => void;
};

const AcceptPopInClinicSuggestionButton = ({
  requestId,
  prepResponsibility,
  disabled,
  onScheduleRefresh,
}: AcceptPopInSuggestionProps) => {
  const styles = useStyles();

  const { apolloClient } = useContext(ApolloClientContext);
  const { setSideVisit } = useContext(SideVisitContext);
  const [showRefreshDialog, setShowRefreshDialog] = useState<boolean>(false);
  const router = useRouterStore();
  const enableObservability = useFeatureFlag(OBSERVABILITY_MY_DAY_TODAY);
  const sendStaffEvent = useObservability(enableObservability);
  const providerId = useProviderId();

  const handleAccept = useCallback(
    async (id: string) => {
      if (!apolloClient) {
        return;
      }

      try {
        const response = await apolloClient.mutate<AcceptMutationResponse, AcceptMutationArgs>({
          mutation: ACCEPT_MUTATION,
          variables: { requestId: id, prepResponsibility },
        });

        sendStaffEvent({
          eventType: 'myDayToday:acceptPopInEvent',
          userId: providerId ?? null,
          details: {
            requestId: id,
          },
        });

        const eventId = response.data?.acceptDropInClinicRequest.eventId;
        const patientId = response.data?.acceptDropInClinicRequest.patientId;
        if (eventId && patientId) {
          // Show the visit note in the right SideVisit pane, then navigate this same tab to the
          // patient page. We don't pin the tab: patientShow's defaultTab lands snapshot-eligible
          // providers on the Clinical Snapshot (left/main column) and falls back to Overview when
          // the CLINICAL_SNAPSHOT flag is off, so the left pane is never blank. We no longer open
          // the video call here — see ORCA-2449.
          setSideVisit(patientId, eventId);
          router.goTo('showPatient', { params: { id: patientId } });
        } else {
          setShowRefreshDialog(true);
        }
      } catch (e) {
        setShowRefreshDialog(true);
      }
    },
    [
      apolloClient,
      setShowRefreshDialog,
      router,
      setSideVisit,
      prepResponsibility,
      providerId,
      sendStaffEvent,
    ],
  );

  const handleRefreshSchedule = useCallback(async () => {
    if (!apolloClient) {
      return;
    }

    try {
      await apolloClient.mutate({
        mutation: REFRESH_SCHEDULE_MUTATION,
      });
      setShowRefreshDialog(false);
      onScheduleRefresh();
    } catch (e) {
      console.error('Failed to refresh schedule:', e);
    }
  }, [apolloClient, onScheduleRefresh]);

  return (
    <div className={styles.details}>
      <div style={{ display: 'flex', flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
        <Tooltip title={disabled ? 'Visit not ready to be accepted yet' : ''}>
          <span>
            <Button
              className={styles.acceptButton}
              onClick={() => handleAccept(requestId)}
              disabled={disabled}
            >
              <Videocam />
              Accept and Start
            </Button>
          </span>
        </Tooltip>
      </div>
      {showRefreshDialog && (
        <ConfirmDialog
          title="Patient Unavailable"
          cancelLabel="Dismiss"
          onCancel={() => setShowRefreshDialog(false)}
          onSubmit={handleRefreshSchedule}
          submitLabel="Refresh now"
        >
          The patient you tried to accept is no longer available. They may have been accepted by
          another provider or left the queue. Click Refresh Now to get your next patient.
        </ConfirmDialog>
      )}
    </div>
  );
};

const useStyles = makeStyles({
  details: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    alignItems: 'stretch',
    gap: 8,
    flexGrow: 1,
  },
  acceptButton: {
    backgroundColor: BoulderColors.Peach5,
    color: 'white',
    alignSelf: 'flex-end',
    '& svg': {
      marginRight: 6,
    },
    '&:hover': {
      backgroundColor: BoulderColors.Peach5,
    },
  },
});

export default AcceptPopInClinicSuggestionButton;
